import { getRingServiceLevelsFromOnlineModel } from '../../components/AttachRing/attachRingDataConverter.js';
import { isMobileVoiceAddOn } from '../../components/SubscriptionDetails/addOnDependencyUtils.js';
import { SubscriptionPbxDetails, SubscriptionType } from '../../generated/api/models.js';
import type { ServiceLevelAddon } from '../../components/AttachRing/ServiceLevelAndAddonsInterfaces.js';
import type { ChangeOfferAddons } from '../../components/OrderSubscriptionSelection/subscriptionChangeConfirmationUtils.js';
import type { AddOnVisibility, CompanyInfoResponse, OnlineModel, Subscription } from '../../generated/api/models.js';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { AddedAddon } from '../types/addon.js';

export const getOpeningFeeAddons = (
  inputAddons: ServiceLevelAddon[],
  dependencies?: DependencyRecord[],
  associations?: AssociationRecord[]
) => {
  const RING_OPENING_FEES_TITLE = 'Elisa Ring avausmaksut';
  const DEPENDENCY_TYPE_INCLUSION = 'Inclusion';
  const DEPENDENCY_TYPE_MANDATORY = 'Mandatory';

  const pbxServiceLeveladdonCodes = inputAddons.map(addOn => {
    return addOn.addonCode || undefined;
  });

  const pbxServiceLevelDependencies = dependencies?.filter(dep => {
    return (
      pbxServiceLeveladdonCodes?.includes(dep.add_on_target__r?.guid__c) &&
      (dep.type__c === DEPENDENCY_TYPE_INCLUSION || dep.type__c === DEPENDENCY_TYPE_MANDATORY)
    );
  });

  const openingFeeAssociationIds = pbxServiceLevelDependencies?.map(dep => dep.add_on_target__r?.id);

  const openingFeeAddOns = associations?.filter(assoc => {
    return (
      openingFeeAssociationIds?.includes(assoc.cspmb__add_on_price_item__c) &&
      assoc.cspmb__add_on_price_item__r.cspmb__one_off_charge__c
    );
  });

  const subscriptionOpeningFeeAddons = associations?.filter(assoc => {
    return assoc.cspmb__group__c === RING_OPENING_FEES_TITLE;
  });

  return openingFeeAddOns && subscriptionOpeningFeeAddons && openingFeeAddOns.concat(subscriptionOpeningFeeAddons);
};

export const getPbxServiceLevelsAndAddons = (ringServicesModel?: OnlineModel, companyInfo?: CompanyInfoResponse) => {
  const ringSolution = companyInfo?.pbxSolutions?.find(
    (pbxSolution: Subscription | undefined) => pbxSolution?.subscriptionType === SubscriptionType.MOBILE_PBX
  );

  if (!ringSolution) {
    return {
      pbxServiceLevelAddons: [],
      pbxServiceLevels: [],
    };
  }

  const mobilePbxServiceLevelProduct = ringSolution.details?.mobilePbx?.mobilePbxServiceLevelProduct;
  const productCodes = mobilePbxServiceLevelProduct?.userServiceLevelCommercialProductCodes || [];
  const productAddons = mobilePbxServiceLevelProduct?.addOnServiceLevelSelections || [];

  const serviceLevelsAndAddons = getRingServiceLevelsFromOnlineModel(productCodes, productAddons, ringServicesModel);
  return {
    pbxServiceLevels: serviceLevelsAndAddons.pbxServiceLevels,
    pbxServiceLevelAddons: serviceLevelsAndAddons.pbxServiceLevelAddons,
  };
};

export const isRingSubscription = (subscription: Subscription, pbxSolutions?: Subscription[]): boolean => {
  const isRingEnabledForCompany = pbxSolutions?.some(sub => sub.subscriptionType === SubscriptionType.MOBILE_PBX);
  const isRingEnabledForSub =
    subscription.details?.mobile?.pbxConfiguration?.pbxConfigurationDetails?.pbxType ===
    SubscriptionPbxDetails.PbxTypeEnum.RING;

  return Boolean(isRingEnabledForCompany && isRingEnabledForSub);
};

export const shouldIncludeAddon = (addonCode: string, isRingSub: boolean) =>
  !isRingSub || !isMobileVoiceAddOn(addonCode);

export const filterAddonsFromRingSubs = (addedAddons?: AddedAddon[]): AddedAddon[] =>
  addedAddons?.filter(addon => shouldIncludeAddon(addon.addOnCode, true)) || [];

export const filterVisibilitiesFromRingSubs = (addonVisibilities: AddOnVisibility[]) =>
  addonVisibilities.filter(visibility => shouldIncludeAddon(visibility.addOnGuidCode, true));

export const filterChangeOfferAddonsFromRingSubs = ({
  addOnsToAdd,
  addOnsToUpdate,
  addOnsToRemove,
  isRingSub,
}: ChangeOfferAddons & { isRingSub: boolean }): ChangeOfferAddons => ({
  addOnsToAdd: isRingSub ? filterAddonsFromRingSubs(addOnsToAdd) : addOnsToAdd,
  addOnsToUpdate: isRingSub ? filterAddonsFromRingSubs(addOnsToUpdate) : addOnsToUpdate,
  addOnsToRemove,
});
